/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-function */
import { Component, ElementRef, EventEmitter, HostListener, Inject, Input, OnChanges, Output, PLATFORM_ID, SimpleChanges, ViewChild, ViewEncapsulation, forwardRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { QuillModule } from 'ngx-quill';
import { FormControl, FormsModule, ReactiveFormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PlatformService, QuillEditorEvent } from '@sml/utility';
import { IconsComponent } from '@sml/icons';
import { ToastrService } from '@sml/toastr';

@Component({
  selector: 'sml-text-editor',
  standalone: true,
  imports: [CommonModule, QuillModule, FormsModule, ReactiveFormsModule, IconsComponent,],
  templateUrl: './text-editor.component.html',
  styleUrls: ['./text-editor.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextEditorComponent),
      multi: true,
    },
  ],
})
export class TextEditorComponent implements OnChanges, ControlValueAccessor {
  
  private readonly toastr = inject(ToastrService);

  @ViewChild('editorAreaRef') editorAreaRef!: ElementRef<any>;
  @ViewChild('editorRef') editorRef!: ElementRef<any>;
  @Input() enableLink = true
  @Input() placeholderText = '';
  @Input() size='';
  @Input() showCharacterLimitText = true;
  @Input() characterLimit = 1000;
  @Input() editorMessage = '';
  @Input() editorMessagePosition = 'end';
  @Input() editorMessageIcon = 'info';
  @Input() headerPosition: 'top' | 'bottom' = 'top';
  @Input() focusEditor = false;
  @Input() editorClass = '';
  @Input() editorHeight=false;
  @Output() blurEdit = new EventEmitter<void>();
  @Output() characterCountChange = new EventEmitter<number>();
  public description = '';
  public characterCount = 0;
  public editorControl = new FormControl('');
  public editorInstance: any = null;
  public disabled = false;
  onChange: (value: string) => void = () => { };
  onTouched: () => void = () => { };
  public quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ align: '' }, { align: 'center' }, { align: 'right' }],
      [{ list: 'bullet' }, { list: 'ordered' }],
      ['link']
    ],
  };

  constructor(
    private platformService: PlatformService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {
    
  }



  ngOnChanges(changes: SimpleChanges): void {
    if (changes['focusEditor']?.currentValue && this.editorInstance) {
      this.editorInstance?.focus();
    }
  }

  @HostListener('document:click', ['$event', '$event.target'])
  public onClick(event: MouseEvent, targetElement: HTMLElement): void {
    if (!targetElement) {
      return;
    }
    const clickedInside = this.editorAreaRef?.nativeElement?.contains(targetElement);
    if (!clickedInside) {
      this.blurEdit.emit();
    }
  }

  onContentChanged(event: QuillEditorEvent): void {
    let currentHtml = '';
    this.onTouched();
    if (event.editor.editor.isBlank()) {
      this.characterCount = 0;
      this.editorControl.setValue('');
    } else{
      // console.log('old', event.editor.root.innerHTML.trim())
     if(event?.html){
        currentHtml = event.html.replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
        // Reduce all font sizes to 4px for consistent output
        currentHtml = this.reduceFontSize(currentHtml);
      }
      const currentText = event.editor.getText();
      const count = currentText.trim().length;
      if (this.showCharacterLimitText && count > this.characterLimit) {
        event.editor.deleteText(this.characterLimit, currentText.length);
      }
      if (currentHtml === '<p><br></p>' || currentHtml === '') {
        currentHtml = '';
      }
      const currentTextT = event.editor.getText();
      this.characterCount = currentTextT.trim().length;
    }
    this.characterCountChange.emit(this.characterCount);
    this.onChange(currentHtml);
  }

  /**
   * Reduce all font sizes to 4px in HTML content
   */
  private reduceFontSize(html: string): string {
    if (!html) return html;

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Traverse all elements and reduce font sizes
    const processNode = (node: Node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        
        // Set font size to 16px 
        element.style.fontSize = '16px';
        element.style.lineHeight = 'inherit';
      }

      // Process child nodes
      Array.from(node.childNodes).forEach(child => processNode(child));
    };

    processNode(tempDiv);
    return tempDiv.innerHTML;
  }

  onEditorCreated(editor: any): void {
    this.editorInstance = editor;
    this.initializeEditor(editor);
  
    editor.root.addEventListener('keydown', (e: KeyboardEvent) => this.handleKeyDown(e, editor));
  
    // Listen for text-change events
    editor.on('text-change', (delta: any, _oldDelta: any, source: any) => {
      if (source === 'user') {
        this.handleTextChange(editor, delta);
      }
    });
  
    if (this.focusEditor) {
      this.editorInstance?.focus();
    }
  }
  
  private initializeEditor(editor: any): void {
    editor.root.dataset['placeholder'] = this.placeholderText;
    if (this.description) {
      this.updateEditorContent(this.description);
    }
  }
  
  private handleKeyDown(event: KeyboardEvent, editor: any): void {
    const isAtMaxLength = this.showCharacterLimitText && editor.getText().trim().length >= this.characterLimit;
    const isAllowedKey = ['Backspace', 'Delete', 'Home', 'End'].includes(event.key) || event.key.includes('Arrow') || event.ctrlKey || event.metaKey;
  
    if (isAtMaxLength && !isAllowedKey) {
      event.preventDefault();
    }
  }
  
  private handleTextChange(editor: any, delta: any): void {
    const addedLinks = this.getLinksFromDelta(delta);
    if (addedLinks.length > 0 && !addedLinks.every((url) => this.validateUrl(url))) {
      this.preventInvalidLinkChange(editor, delta);
    }
  }
  
  // Validate the URL to ensure it starts with http:// or https://
   private validateUrl(url: string): boolean {
    // URL regex (checks for http or https)
    const urlRegex = /^https?:\/\/[^\s$.?#].[^\s]*$/i;

    // Email regex (basic but effective)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    return urlRegex.test(url) || emailRegex.test(url);
  }

  // Utility method to extract link URLs from the delta
  private getLinksFromDelta(delta: any): string[] {
    const links: string[] = [];
    delta.ops.forEach((op: any) => {
      if (op.attributes && op.attributes.link) {
        links.push(op.attributes.link);
      }
    });
    return links;
  }


  // Prevent the invalid link change from being applied to the editor
  private preventInvalidLinkChange(editor: any, delta: any) {
    editor.history.undo();
    
    this.toastr.error({
      message: 'Invalid URL. Please ensure it starts with http:// or https://',
      title: '',
      icon: 'error',
      close: false,
      // delay: 0
    });
  }

  handlePasteToResetContent(event: ClipboardEvent): void {
    if (this.editorInstance) {
      const clipboardData = event.clipboardData || (window as any).clipboardData;
      const pastedData = clipboardData.getData('text/plain');
      if (pastedData) {
        this.editorInstance.clipboard.dangerouslyPasteHTML(pastedData);
        event.preventDefault();
      }
    }
  }

  updateEditorContent(content: string): void {
    if (this.editorInstance) {
      this.editorControl.patchValue(content);
     
    }
  }

  writeValue(value: string): void {
    this.description = value;
    this.updateEditorContent(value);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
}
